import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CalendarIcon, ChevronLeft, ChevronRight, Download, Plus, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errText } from "@/lib/api";
import { downloadSheetPdf, openPrintSheet } from "@/lib/exportSheet";
import { moveEvent } from "@/lib/moveEvent";
import EventDialog from "@/components/EventDialog";
import { EntryActionsDialog } from "@/components/EntryActionsDialog";
import { EventRow } from "@/components/EventRow";
import { VoiceLogDialog } from "@/components/VoiceLogDialog";
import { WeekBoard } from "@/components/WeekBoard";
import { DayTimeline } from "@/components/DayTimeline";
import { TodoPanel } from "@/components/TodoPanel";
import { EdgeNav } from "@/components/EdgeNav";
import { useSwipe } from "@/lib/useSwipe";
import { ForumFilter } from "@/components/ForumFilter";
import { alpha } from "@/lib/forumColors";
import {
  addDays,
  addMonths,
  isAllDay,
  isoDate,
  longDate,
  morningLabel,
  parseISO,
  shortDate,
  startOfWeek,
} from "@/lib/eventMeta";
import { useAuth } from "@/context/AuthContext";
import { useForumColors } from "@/context/ForumColorContext";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const rangeFor = (view, anchor) => {
  if (view === "day") return [isoDate(anchor), isoDate(anchor)];
  if (view === "week") {
    const s = startOfWeek(anchor);
    return [isoDate(s), isoDate(addDays(s, 6))];
  }
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const last = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
  return [isoDate(startOfWeek(first)), isoDate(addDays(startOfWeek(last), 6))];
};

export default function CalendarPage() {
  const { canWrite, workspace, role, user } = useAuth();
  const navigate = useNavigate();
  const { visibleEvents, slicesFor } = useForumColors();
  const [params, setParams] = useSearchParams();
  const [view, setView] = useState(
    params.get("view") || user?.preferences?.landing_view || "week",
  );
  const [anchor, setAnchor] = useState(
    params.get("date") ? parseISO(params.get("date")) : new Date(),
  );
  const [events, setEvents] = useState([]);
  const [heat, setHeat] = useState({});
  const [todoCells, setTodoCells] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [menuEvent, setMenuEvent] = useState(null);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(isoDate(new Date()));
  const [voiceEvent, setVoiceEvent] = useState(null);

  const [start, end] = useMemo(() => rangeFor(view, anchor), [view, anchor]);

  const load = useCallback(async () => {
    if (role === "clerk") return;
    try {
      const [ev, hm, td] = await Promise.all([
        api.get("/events", { params: { start, end } }),
        api.get("/calendar/heatmap", { params: { start, end } }),
        api.get("/todos/summary", { params: { start, end } }),
      ]);
      setEvents(ev.data);
      setHeat(Object.fromEntries(hm.data.cells.map((c) => [c.date, c])));
      setTodoCells(Object.fromEntries(td.data.cells.map((c) => [c.date, c])));
    } catch (e) {
      toast.error(errText(e));
    }
  }, [start, end, workspace?.workspace_id, role]);

  useEffect(() => {
    load();
    const handler = () => load();
    window.addEventListener("diary-refresh", handler);
    return () => window.removeEventListener("diary-refresh", handler);
  }, [load]);

  useEffect(() => {
    setParams({ view, date: isoDate(anchor) }, { replace: true });
  }, [view, anchor, setParams]);

  const byDate = useMemo(() => {
    const map = {};
    visibleEvents(events).forEach((e) => {
      (map[e.date] = map[e.date] || []).push(e);
    });
    return map;
  }, [events, visibleEvents]);

  const openEvent = (ev) => setMenuEvent(ev);

  // a link can land here while the page is already open — follow the params, not just the first mount
  const [wantedEntry, setWantedEntry] = useState(params.get("entry"));
  useEffect(() => {
    const wantView = params.get("view");
    const wantDate = params.get("date");
    const wantEntry = params.get("entry");
    if (wantView && wantView !== view) setView(wantView);
    if (wantDate && wantDate !== isoDate(anchor)) setAnchor(parseISO(wantDate));
    if (wantEntry) setWantedEntry(wantEntry);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    if (!wantedEntry) return;
    const found = events.find((e) => e.event_id === wantedEntry);
    if (found) {
      setWantedEntry(null);
      setMenuEvent(found);
    }
  }, [events, wantedEntry]);

  const editEvent = (ev) => {
    setMenuEvent(null);
    setEditing(ev);
    setDialogOpen(true);
  };

  const openNew = (date) => {
    setEditing(null);
    setSelected(date || isoDate(anchor));
    setDialogOpen(true);
  };

  const [showHint, setShowHint] = useState(
    () => localStorage.getItem("chambers-swipe-hint") !== "1",
  );

  const dismissHint = useCallback(() => {
    localStorage.setItem("chambers-swipe-hint", "1");
    setShowHint(false);
  }, []);

  const step = useCallback(
    (dir) => {
      if (view === "day") setAnchor((a) => addDays(a, dir));
      else if (view === "week") setAnchor((a) => addDays(a, dir * 7));
      else setAnchor((a) => addMonths(a, dir));
    },
    [view],
  );

  const swipe = useSwipe(
    useCallback(() => {
      step(1);
      dismissHint();
    }, [step, dismissHint]),
    useCallback(() => {
      step(-1);
      dismissHint();
    }, [step, dismissHint]),
  );

  const monthCells = useMemo(() => {
    if (view !== "month") return [];
    const cells = [];
    let cursor = parseISO(start);
    const last = parseISO(end);
    while (cursor <= last) {
      cells.push(isoDate(cursor));
      cursor = addDays(cursor, 1);
    }
    return cells;
  }, [view, start, end]);

  const weekData = useMemo(() => {
    if (view !== "week") return [];
    const s = startOfWeek(anchor);
    return Array.from({ length: 7 }, (_, i) => {
      const date = isoDate(addDays(s, i));
      return { date, cell: heat[date], events: byDate[date] || [] };
    });
  }, [view, anchor, heat, byDate]);

  const headerLabel =
    view === "day"
      ? longDate(anchor)
      : view === "week"
        ? `${shortDate(isoDate(startOfWeek(anchor)))} — ${shortDate(isoDate(addDays(startOfWeek(anchor), 6)))}`
        : anchor.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  const dayList = byDate[isoDate(anchor)] || [];
  const courtish = dayList.filter((e) => ["fixed_time", "session_based"].includes(e.event_type));
  const lanes = [
    [morningLabel(dayList), dayList.filter((e) => e.event_type === "morning"), "morning"],
    ["Session 1 — pre-lunch", courtish.filter((e) => ["session_1", "both"].includes(e.derived_session)), "s1"],
    ["Session 2 — post-lunch", courtish.filter((e) => ["session_2", "both"].includes(e.derived_session)), "s2"],
    ["Non-court hearings", dayList.filter((e) => e.event_type === "non_court"), "non-court"],
    ["Post-court", dayList.filter((e) => e.event_type === "post_court"), "post"],
    ["All day & out-of-station", dayList.filter((e) => isAllDay(e)), "oos"],
  ];

  return (
    <div className="space-y-8" data-testid="calendar-page" {...swipe}>
      <EdgeNav onPrev={() => step(-1)} onNext={() => step(1)} label={view} />

      <div
        data-testid="calendar-toolbar"
        style={{ top: "var(--topbar-h, 4rem)" }}
        className="sticky z-20 -mx-4 sm:-mx-6 px-4 sm:px-6 py-2.5 bg-background/95 backdrop-blur-md border-b border-border/70 md:static md:mx-0 md:px-0 md:py-0 md:bg-transparent md:backdrop-blur-none md:border-0 print:static print:bg-transparent"
      >
        <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <button
            data-testid="calendar-prev"
            onClick={() => step(-1)}
            className="h-11 w-11 grid place-items-center border border-border rounded-sm hover:bg-accent transition-colors duration-200"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            data-testid="calendar-next"
            onClick={() => step(1)}
            className="h-11 w-11 grid place-items-center border border-border rounded-sm hover:bg-accent transition-colors duration-200"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                data-testid="calendar-date-picker"
                className="rounded-full h-11 px-4 text-sm sm:h-12 sm:px-6 sm:text-base font-medium max-w-[40vw] sm:max-w-none"
              >
                <CalendarIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2 shrink-0" />
                <span className="truncate">{headerLabel}</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-auto p-0">
              <Calendar mode="single" selected={anchor} onSelect={(d) => d && setAnchor(d)} initialFocus />
            </PopoverContent>
          </Popover>
          <Button
            variant="outline"
            data-testid="calendar-today-btn"
            onClick={() => {
              setAnchor(new Date());
              setView("day");
            }}
            className="rounded-sm h-11"
          >
            Today
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <Tabs value={view} onValueChange={setView}>
            <TabsList className="rounded-sm">
              <TabsTrigger
                value="day"
                data-testid="view-day"
                className="rounded-sm px-2.5 sm:px-4 text-sm sm:text-base"
              >
                Day
              </TabsTrigger>
              <TabsTrigger
                value="week"
                data-testid="view-week"
                className="rounded-sm px-2.5 sm:px-4 text-sm sm:text-base"
              >
                Week
              </TabsTrigger>
              <TabsTrigger
                value="month"
                data-testid="view-month"
                className="rounded-sm px-2.5 sm:px-4 text-sm sm:text-base"
              >
                Month
              </TabsTrigger>
            </TabsList>
          </Tabs>
          {canWrite ? (
            <Button
              data-testid="calendar-new-entry-btn"
              onClick={() => openNew(isoDate(anchor))}
              className="rounded-full h-11 w-11 sm:w-auto sm:px-5 p-0 sm:p-2"
              title="New diary entry"
            >
              <Plus className="h-5 w-5 sm:mr-1" />
              <span className="hidden sm:inline">Entry</span>
            </Button>
          ) : null}
          <button
            data-testid="calendar-print-btn"
            title="Print this sheet"
            onClick={() => openPrintSheet(view, isoDate(anchor))}
            className="h-10 w-10 sm:h-11 sm:w-11 grid place-items-center border border-border rounded-sm hover:bg-accent transition-colors duration-200"
          >
            <Printer className="h-5 w-5" />
          </button>
          <button
            data-testid="calendar-pdf-btn"
            title="Download PDF"
            onClick={() =>
              downloadSheetPdf(view, isoDate(anchor)).catch((e) => toast.error(errText(e)))
            }
            className="h-10 w-10 sm:h-11 sm:w-11 grid place-items-center border border-border rounded-sm hover:bg-accent transition-colors duration-200"
          >
            <Download className="h-5 w-5" />
          </button>
        </div>
        </div>
        {showHint ? (
          <p
            data-testid="calendar-swipe-hint"
            className="md:hidden mt-1.5 text-xs text-muted-foreground"
          >
            Swipe left or right to move {view === "day" ? "a day" : `a ${view}`}
          </p>
        ) : null}
      </div>

      <ForumFilter />

      {view === "month" ? (
        <div className="surface overflow-hidden" data-testid="month-grid">
          <div className="grid grid-cols-7 border-b border-border">
            {WEEKDAYS.map((d) => (
              <div key={d} className="px-3 py-3 label-caps text-center">
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {monthCells.map((day) => {
              const cell = heat[day];
              const list = byDate[day] || [];
              const slices = slicesFor(list);
              const dominant = slices[0]?.color;
              const inMonth = parseISO(day).getMonth() === anchor.getMonth();
              const isToday = day === isoDate(new Date());
              const total = slices.reduce((sum, f) => sum + f.count, 0) || 1;
              return (
                <button
                  key={day}
                  data-testid={`month-cell-${day}`}
                  onClick={() => {
                    setAnchor(parseISO(day));
                    setView("day");
                  }}
                  className={`min-h-[7.5rem] text-left p-2 border-r border-b border-border relative hover:border-foreground transition-colors duration-200 ${
                    inMonth ? "" : "opacity-45"
                  }`}
                  style={dominant ? { background: alpha(dominant, 0.08) } : undefined}
                >
                  <span className="flex items-center justify-between">
                    <span
                      className={`text-lg font-semibold ${
                        isToday ? "bg-primary text-primary-foreground px-2 rounded-sm" : ""
                      }`}
                    >
                      {parseISO(day).getDate()}
                    </span>
                    <span className="flex items-center gap-1">
                      {cell?.full_day ? (
                        <span
                          data-testid={`month-fullday-${day}`}
                          className="text-[0.6rem] font-bold uppercase tracking-wider text-foreground/70"
                        >
                          all day
                        </span>
                      ) : null}
                      {cell?.urgent ? <span className="h-2 w-2 rounded-full dot-fixed" /> : null}
                      {cell?.clashes ? (
                        <span
                          data-testid={`month-clash-${day}`}
                          className="text-[0.65rem] font-bold text-fixed leading-none"
                          title={`${cell.clashes} overlapping fixed slots`}
                        >
                          !
                        </span>
                      ) : null}
                    </span>
                  </span>

                  {slices.length ? (
                    <span
                      data-testid={`month-bar-${day}`}
                      className="mt-1.5 flex h-2 w-full overflow-hidden rounded-sm"
                    >
                      {slices.map((f) => (
                        <span
                          key={f.key}
                          style={{ background: f.color, width: `${(f.count / total) * 100}%` }}
                          title={`${f.name} · ${f.count}`}
                        />
                      ))}
                    </span>
                  ) : null}

                  <span className="mt-1.5 block space-y-1">
                    {list.slice(0, 2).map((e) => (
                      <span
                        key={e.event_id}
                        className="block text-xs font-medium truncate border-l-[3px] pl-1.5 py-0.5"
                        style={{
                          borderLeftColor: e.court_color || dominant || "currentColor",
                        }}
                      >
                        {e.start_time ? `${e.start_time} · ` : ""}
                        {e.title}
                      </span>
                    ))}
                    {list.length > 2 ? (
                      <span className="block text-xs font-semibold">+{list.length - 2} more</span>
                    ) : null}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {view === "week" ? (
        <WeekBoard
          days={weekData}
          canWrite={canWrite}
          onOpenDay={(date) => {
            setAnchor(parseISO(date));
            setView("day");
          }}
          onOpenEvent={openEvent}
          onAdd={openNew}
          onMove={(event, date) => moveEvent(event, date, load)}
          todos={todoCells}
        />
      ) : null}

      {view === "day" ? (
        <div className="surface p-6 space-y-8" data-testid="day-view">
          <div className="flex items-baseline justify-between gap-4">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold">{longDate(anchor)}</h2>
            <span className="label-caps">{dayList.length} listings</span>
          </div>

          <DayTimeline events={dayList} onOpenEvent={openEvent} />

          <TodoPanel date={isoDate(anchor)} variant="compact" />

          {lanes.map(([label, list, key]) => (
            <div key={key} data-testid={`day-lane-${key}`}>
              <div className="flex items-center justify-between mb-3">
                <p className="label-caps">
                  {label} · {list.length}
                </p>
                {canWrite ? (
                  <button
                    data-testid={`day-add-${key}`}
                    onClick={() => openNew(isoDate(anchor))}
                    className="text-sm font-medium hover:underline"
                  >
                    + Add
                  </button>
                ) : null}
              </div>
              {list.length ? (
                <div className="space-y-3">
                  {list.map((e, i) => (
                    <EventRow
                      key={e.event_id}
                      event={e}
                      index={i}
                      testIdPrefix="day-event"
                      onClick={openEvent}
                      onVoiceLog={canWrite ? setVoiceEvent : undefined}
                      onKit={(ev) => navigate(`/kit?event=${ev.event_id}`)}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground border border-dashed border-border rounded-sm px-4 py-4">
                  Free.
                </p>
              )}
            </div>
          ))}
        </div>
      ) : null}

      <EntryActionsDialog
        event={menuEvent}
        onOpenChange={(v) => !v && setMenuEvent(null)}
        onEdit={editEvent}
        onChanged={load}
      />

      <EventDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        event={editing}
        defaultDate={selected}
        onSaved={load}
        onVoiceLog={canWrite ? setVoiceEvent : undefined}
      />

      <VoiceLogDialog
        open={Boolean(voiceEvent)}
        onOpenChange={(v) => !v && setVoiceEvent(null)}
        event={voiceEvent}
        onSaved={load}
      />
    </div>
  );
}
